import { useMemo } from "react";
import { decodeTime } from "ulid";

import DocumentationButton from "components/DocumentationButton";
import { EmptystateWebhooksColored } from "components/icons/generated";
import ListEntitiesNew from "components/ListEntitiesNew";
import SortableTable from "components/SortableTable";
import { WebhooksTierInfo } from "components/TierInfo/WebhooksTierInfo";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryOrderDirection, WebhooksIntegration } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import Tooltip from "ds/components/Tooltip";
import { withTestId } from "utils/withTestId";

import StackSettingsFormWrapper from "../../components/FormWrapper";
import { StackSettingsContextData } from "../../Context";
import StackSettingsIntegrationsWrapper from "../components/Wrapper";
import { SORTABLE_TABLE_COLUMNS, SORTABLE_TABLE_COLUMNS_ORDER } from "./constants";
import { showStackSettingsIntegrationsWebhooksCreateFormDrawer } from "./CreateFormDrawer";
import { showStackSettingsIntegrationsWebhooksDetails } from "./Details";
import StackSettingsIntegrationsWebhooksListItemVirtualized from "./ListItem/Virtualized";

const StackSettingsIntegrationsWebhooks = () => {
  const { stackSettings, canUpdateStack } = useTypedContext(StackSettingsContextData);

  const webhooks = useMemo(() => {
    const items = stackSettings?.integrations?.webhooks || [];

    return items.map((item) => ({
      ...item,
      createdAt: decodeTime(item.id) / 1000,
    }));
  }, [stackSettings?.integrations?.webhooks]);

  const handleOnDetailsClick = (item: WebhooksIntegration) => {
    showStackSettingsIntegrationsWebhooksDetails({
      id: item.id,
      webhook: item,
    });
  };

  const handleOnCreateClick = () => {
    showStackSettingsIntegrationsWebhooksCreateFormDrawer({
      stackId: stackSettings.id,
    });
  };

  const handleOnEditClick = (item: WebhooksIntegration) => {
    showStackSettingsIntegrationsWebhooksCreateFormDrawer({
      id: item.id,
      webhook: item,
      stackId: stackSettings.id,
    });
  };

  const isCreateButtonVisible = !!canUpdateStack.message || canUpdateStack.result;

  return (
    <StackSettingsIntegrationsWrapper
      actions={
        isCreateButtonVisible ? (
          <Tooltip
            active={!!canUpdateStack.message}
            on={(props) => (
              <Button
                {...props}
                {...withTestId("create-webhook-button")}
                variant="primary"
                disabled={!canUpdateStack.result}
                onPress={handleOnCreateClick}
              >
                Create webhook
              </Button>
            )}
          >
            {canUpdateStack.message}
          </Tooltip>
        ) : null
      }
    >
      <WebhooksTierInfo />
      <StackSettingsFormWrapper padding="0" gap="0" withLimitWidth={false}>
        {webhooks.length > 0 && (
          <SortableTable
            ariaLabel="Webhooks"
            items={webhooks}
            columns={SORTABLE_TABLE_COLUMNS}
            columnOrder={SORTABLE_TABLE_COLUMNS_ORDER}
            renderItems={(items) => (
              <ListEntitiesNew
                itemCount={items.length}
                itemProps={{
                  items,
                  stackId: stackSettings.id,
                  onEditClick: handleOnEditClick,
                  onDetailsClick: handleOnDetailsClick,
                  canUpdateStack,
                }}
                virtualizedItem={StackSettingsIntegrationsWebhooksListItemVirtualized}
                itemKey={(index) => items[index].id}
              />
            )}
            initialSortBy="id"
            initialDirection={SearchQueryOrderDirection.Desc}
          />
        )}

        {webhooks.length === 0 && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateWebhooksColored}
              title="You do not have any webhooks yet"
              caption="Set up a webhook for your current stack to send events to a http endpoint of your choice."
            >
              <Box gap="medium">
                {(canUpdateStack.result || !!canUpdateStack.message) && (
                  <Tooltip
                    active={!!canUpdateStack.message}
                    on={(props) => (
                      <Button
                        {...props}
                        variant="primary"
                        onPress={handleOnCreateClick}
                        disabled={!canUpdateStack.result}
                      >
                        Create webhook
                      </Button>
                    )}
                  >
                    {canUpdateStack.message}
                  </Tooltip>
                )}
                <DocumentationButton
                  to={getDocsUrl("/integrations/webhooks")}
                  label="Documentation"
                />
              </Box>
            </EmptyState>
          </Box>
        )}
      </StackSettingsFormWrapper>
    </StackSettingsIntegrationsWrapper>
  );
};

export default StackSettingsIntegrationsWebhooks;
